import { LoadingSpinner, Modal, PDFViewer } from "@/components";
import { MimeType } from "@/types";
import { cn, getExtensionFromUrl } from "@/utils";
import { renderAsync } from "docx-preview";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { MdClose } from "react-icons/md";
import * as XLSX from "xlsx";
import "./DocumentPreviewModal.css";

interface PreviewAttachment {
  url: string;
  type?: string;
  name?: string;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  attachment: PreviewAttachment | null;
}

type PreviewKind = "pdf" | "docx" | "sheet" | null;

const getFileExtension = (attachment: PreviewAttachment) => {
  const fromName = attachment.name?.split(".").pop()?.toLowerCase() || "";
  if (fromName && !fromName.includes("/")) return fromName.split("?")[0];
  return getExtensionFromUrl(attachment.url).split("?")[0];
};

const getPreviewKind = (attachment: PreviewAttachment): PreviewKind => {
  const ext = getFileExtension(attachment);
  const type = attachment.type;

  if (type === MimeType.PDF || ext === "pdf") {
    return "pdf";
  }

  if (type === MimeType.DOCX || ext === "docx") {
    return "docx";
  }

  if (
    type === MimeType.XLSX ||
    type === MimeType.XLS ||
    type === MimeType.CSV ||
    ["xls", "xlsx", "csv"].includes(ext)
  ) {
    return "sheet";
  }

  return null;
};

const DocumentPreviewModal: React.FC<Props> = ({
  open,
  onOpenChange,
  attachment,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const attachmentUrl = attachment?.url;
  const attachmentName = attachment?.name;
  const attachmentType = attachment?.type;

  const previewKind = useMemo(() => {
    if (!attachmentUrl) return null;
    return getPreviewKind({
      url: attachmentUrl,
      name: attachmentName,
      type: attachmentType,
    });
  }, [attachmentUrl, attachmentName, attachmentType]);

  useEffect(() => {
    if (!open || !attachmentUrl || !previewKind) {
      setIsLoading(false);
      setError(null);
      return;
    }

    // PDF: PDFViewer loads URL itself — no pre-fetch needed
    if (previewKind === "pdf") {
      setIsLoading(false);
      setError(null);
      return;
    }

    let cancelled = false;

    const renderPreview = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const response = await fetch(attachmentUrl);
        if (!response.ok) {
          throw new Error("Failed to fetch file");
        }

        const buffer = await response.arrayBuffer();
        if (cancelled) return;

        const container = containerRef.current;
        if (!container) return;
        container.innerHTML = "";

        if (previewKind === "docx") {
          await renderAsync(buffer, container, undefined, {
            className: "docx-preview",
            inWrapper: true,
            ignoreWidth: true,
            breakPages: true,
          });
        } else {
          const workbook = XLSX.read(buffer, { type: "array" });
          const firstSheetName = workbook.SheetNames[0];
          if (!firstSheetName) {
            throw new Error("Empty workbook");
          }

          const sheet = workbook.Sheets[firstSheetName];
          const html = XLSX.utils.sheet_to_html(sheet);
          container.innerHTML = html;

          const table = container.querySelector("table");
          if (table) {
            table.className =
              "min-w-full border-collapse text-sm text-light_primary";
            table.querySelectorAll("td, th").forEach((cell) => {
              cell.setAttribute(
                "class",
                "border border-grey-300 px-2 py-1 align-top whitespace-pre-wrap",
              );
            });
            table.querySelectorAll("th").forEach((cell) => {
              cell.classList.add("bg-grey-100", "font-semibold");
            });
          }

          if (workbook.SheetNames.length > 1) {
            const note = document.createElement("p");
            note.className = "text-xs text-light_secondary mb-3";
            note.textContent = `シート: ${firstSheetName}（全${workbook.SheetNames.length}シート中の1シート目を表示）`;
            container.prepend(note);
          }
        }
      } catch {
        if (!cancelled) {
          setError(
            "ファイルのプレビューに失敗しました。ダウンロードしてご確認ください。",
          );
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    void renderPreview();

    return () => {
      cancelled = true;
      if (containerRef.current) {
        containerRef.current.innerHTML = "";
      }
    };
  }, [open, attachmentUrl, attachmentName, previewKind]);

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={
        <div className="flex items-center justify-between gap-4 w-full">
          <span className="truncate" title={attachment?.name || "プレビュー"}>
            {attachment?.name || "プレビュー"}
          </span>
          <button
            type="button"
            className="shrink-0 p-1 rounded-full hover:bg-grey-200 transition-colors"
            onClick={() => onOpenChange(false)}
            aria-label="閉じる"
            title="閉じる"
          >
            <MdClose className="size-6 text-light_primary" />
          </button>
        </div>
      }
      fullScreen
      overflowScroll={false}
      contentClassName="bg-white h-full"
      className="bg-white"
    >
      <div className="relative h-full min-h-0 overflow-hidden">
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-white/80 z-10">
            <LoadingSpinner />
          </div>
        )}

        {error ? (
          <div className="flex items-center justify-center h-full text-sm text-error_main">
            {error}
          </div>
        ) : previewKind === "pdf" && attachmentUrl ? (
          <PDFViewer
            file={attachmentUrl}
            width="100%"
            height="100%"
            toolbar={1}
            scrollbar={1}
            className="h-full [&_iframe]:!h-full [&_iframe]:min-h-[calc(100vh-120px)]"
          />
        ) : (
          <div
            ref={containerRef}
            className={cn(
              "h-full min-h-0 overflow-auto bg-white document-preview-scroll",
              previewKind === "docx" && "docx-preview-container",
            )}
          />
        )}
      </div>
    </Modal>
  );
};

export default DocumentPreviewModal;
export { getPreviewKind, getFileExtension };
export type { PreviewAttachment };
